import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    type MutableRefObject,
    type RefObject,
} from "react";

// Насколько раньше верхней кромки списка просим следующую порцию. Запас в
// экран с небольшим хвостом: к моменту, когда пользователь домотает до первого
// показанного сообщения, порция обычно уже приехала и стык не заметен.
const PRELOAD_MARGIN_PX = 600;

// Сколько держим позицию после вставки, пока медиа догруженных сообщений
// добирают высоту. Замер на чате из 217 сообщений: без удержания лента доезжала
// ещё на ~560px после того, как порция уже была на месте.
const STABILIZE_MS = 2000;

/** Сообщение, по которому держим позицию, и его отступ от верхней кромки. */
interface ReadingAnchor {
    messageId: string;
    offset: number;
}

function messageAnchorSelector(messageId: string): string {
    return `[data-msg-anchor="${CSS.escape(messageId)}"]`;
}

/** Первое сообщение, начинающееся ниже верхней кромки, — то, что человек читает. */
function measureAnchor(container: HTMLDivElement): ReadingAnchor | null {
    const containerTop = container.getBoundingClientRect().top;
    for (const el of container.querySelectorAll<HTMLElement>("[data-msg-anchor]")) {
        const messageId = el.dataset.msgAnchor;
        if (!messageId) continue;
        const top = el.getBoundingClientRect().top;
        if (top >= containerTop) return { messageId, offset: top - containerTop };
    }
    return null;
}

/**
 * Вернуть якорное сообщение на прежнее место. Смещаем scrollTop на дрейф, а не
 * на прирост высоты: вставка сверху — не единственное, что меняет высоту, и
 * компенсация «по приросту» ловила бы ещё и медиа, догружающиеся НИЖЕ экрана,
 * толкая ленту вниз тем сильнее, чем дальше она уехала.
 */
function restoreAnchor(container: HTMLDivElement, anchor: ReadingAnchor): void {
    const el = container.querySelector<HTMLElement>(messageAnchorSelector(anchor.messageId));
    if (!el) return;
    const drift = el.getBoundingClientRect().top - container.getBoundingClientRect().top - anchor.offset;
    if (Math.abs(drift) < 1) return;
    container.scrollTop += drift;
}

interface UseLoadOlderMessagesParams {
    /** Контейнер со скроллом (он же root для наблюдателя). */
    scrollRef: RefObject<HTMLDivElement | null>;
    /** Длина ленты — по её приросту ловим момент вставки сверху. */
    messageCount: number;
    /** Есть ли что догружать (курсор ещё не дошёл до начала чата). */
    hasMore: boolean;
    /** Порция уже в пути. */
    isLoading: boolean;
    onLoadMore: () => void;
    /**
     * Поднимается на время «просили порцию → вставили → вернули позицию».
     * `useStickyScrollAnchor` смотрит на него, чтобы не принять прирост высоты
     * сверху за новое сообщение и не утащить пользователя вниз.
     */
    isPrependingRef: MutableRefObject<boolean>;
    /**
     * Флаг «пользователь у низа ленты» из `useStickyScrollAnchor`. Догрузка его
     * снимает: тот, кто домотал до истории, смотрит середину чата, а не хвост.
     */
    isStickyBottomRef: MutableRefObject<boolean>;
}

/**
 * Догрузка более ранних сообщений по прокрутке вверх.
 *
 * Две задачи. Первая — вовремя попросить порцию: sentinel над лентой + наблюдение
 * за пересечением с запасом `PRELOAD_MARGIN_PX`. Вторая, более важная, — удержать
 * позицию чтения: вставка сверху увеличивает высоту контента, а `scrollTop`
 * остаётся прежним, поэтому лента визуально прыгает вниз ровно на высоту
 * вставленного. Запоминаем сообщение, на которое смотрит человек, и возвращаем
 * его на место — сразу после вставки и пока догружаются медиа.
 */
export function useLoadOlderMessages({
    scrollRef,
    messageCount,
    hasMore,
    isLoading,
    onLoadMore,
    isPrependingRef,
    isStickyBottomRef,
}: UseLoadOlderMessagesParams) {
    const sentinelRef = useRef<HTMLDivElement>(null);
    // Якорь чтения на момент запроса порции; null — запроса не было.
    const anchorRef = useRef<ReadingAnchor | null>(null);
    const countAtRequestRef = useRef(0);
    const stopStabilizeRef = useRef<(() => void) | null>(null);

    const stopStabilizing = useCallback(() => {
        stopStabilizeRef.current?.();
        stopStabilizeRef.current = null;
    }, []);

    // Пока медиа догруженных сообщений добирают высоту, повторяем возврат к
    // якорю. Фаза короткая и обрывается жестом: дальше человек сам решает, где
    // смотреть, и «помощь» превратилась бы в борьбу за скролл.
    const startStabilizing = useCallback(
        (container: HTMLDivElement, anchor: ReadingAnchor) => {
            stopStabilizing();
            const observer = new ResizeObserver(() => restoreAnchor(container, anchor));
            const content = container.firstElementChild;
            if (content) observer.observe(content);
            const timer = window.setTimeout(() => stopStabilizing(), STABILIZE_MS);
            const stopOnGesture = () => stopStabilizing();
            container.addEventListener("wheel", stopOnGesture, { passive: true });
            container.addEventListener("touchmove", stopOnGesture, { passive: true });

            stopStabilizeRef.current = () => {
                observer.disconnect();
                window.clearTimeout(timer);
                container.removeEventListener("wheel", stopOnGesture);
                container.removeEventListener("touchmove", stopOnGesture);
            };
        },
        [stopStabilizing],
    );

    useEffect(() => stopStabilizing, [stopStabilizing]);

    const requestMore = useCallback(() => {
        const container = scrollRef.current;
        if (!container) return;
        if (anchorRef.current) return; // порция уже запрошена — второй якорь затёр бы первый
        const anchor = measureAnchor(container);
        if (!anchor) return;
        anchorRef.current = anchor;
        countAtRequestRef.current = messageCount;
        isPrependingRef.current = true;
        // Домотал до истории — значит читает не хвост. Без этого sticky-режим
        // (он снимается только жестом прокрутки, а до верха можно доехать и
        // программно — поиском по чату, например) утащит ленту вниз, как только
        // вставка увеличит высоту: ResizeObserver отработает уже следующим
        // кадром, когда флаг вставки снят.
        isStickyBottomRef.current = false;
        onLoadMore();
    }, [scrollRef, messageCount, onLoadMore, isPrependingRef, isStickyBottomRef]);

    // Возврат позиции — до отрисовки кадра (useLayoutEffect), иначе прыжок
    // успевает мелькнуть.
    useLayoutEffect(() => {
        const container = scrollRef.current;
        const anchor = anchorRef.current;
        if (!container || !anchor) return;
        if (messageCount <= countAtRequestRef.current) return; // порция ещё не вставлена
        restoreAnchor(container, anchor);
        anchorRef.current = null;
        isPrependingRef.current = false;
        startStabilizing(container, anchor);
    }, [messageCount, scrollRef, isPrependingRef, startStabilizing]);

    // Пустая порция (сервер отдал меньше лимита ровно в момент запроса) ленту не
    // удлиняет — снимаем якорь, как только загрузка кончилась, иначе он навсегда
    // заблокирует следующие запросы. Проверка на каждый рендер, без списка
    // зависимостей: залипший якорь глушит догрузку до конца сессии, а цена
    // проверки — два сравнения.
    useEffect(() => {
        if (isLoading) return;
        if (!anchorRef.current) return;
        if (messageCount > countAtRequestRef.current) return;
        anchorRef.current = null;
        isPrependingRef.current = false;
    });

    useEffect(() => {
        const container = scrollRef.current;
        const sentinel = sentinelRef.current;
        if (!container || !sentinel) return;
        if (!hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                requestMore();
            },
            { root: container, rootMargin: `${PRELOAD_MARGIN_PX}px 0px 0px 0px` },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
        // messageCount в зависимостях: после вставки порции sentinel уезжает из
        // видимой области, и наблюдатель должен пересчитать пересечение заново.
    }, [scrollRef, hasMore, messageCount, requestMore]);

    return { sentinelRef };
}
